// frontend/src/components/Automations/ScheduledView.jsx
// Unified Scheduled view (Group 6 PR C, §9): one read over automations,
// research tasks and pg-boss system queues (GET /api/scheduled), grouped by
// triage state, with pause/resume/cancel routed to each row's owning service.
import React, { useEffect, useState, useCallback } from 'react';
import { Bot, Search, Server } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.jsx';
import { rowToDisplay, groupRows, GROUP_ORDER, GROUP_LABELS } from './scheduledRows.js';
import './automations.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Per-viewer convenience only — never data the view depends on to render
// correctly. Every access is guarded: a private window, cleared site data or
// a blocked accessor must not break the view, only fall back to collapsed.
const ENGINE_OPEN_KEY = 'solomon-scheduled-engine-open';
function readEngineOpenPref() {
  try { return localStorage.getItem(ENGINE_OPEN_KEY) === 'true'; } catch { return false; }
}
function writeEngineOpenPref(open) {
  try { localStorage.setItem(ENGINE_OPEN_KEY, open ? 'true' : 'false'); } catch { /* per-viewer convenience only */ }
}

const ICONS = { bot: Bot, search: Search, server: Server };

function fmt(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
}

function SchedRow({ row, pendingKey, onAction }) {
  const display = rowToDisplay(row);
  const Icon = ICONS[display.iconKey] || Server;
  const nextRun = row.nextRunBasis === 'unknown' ? '—' : fmt(row.nextRunAt);
  return (
    <tr>
      <td className="sched-cell-name">
        <span className="sched-row-name">
          <Icon className="sched-icon" size={16} strokeWidth={1.5} aria-hidden="true" />
          <span className="sched-row-name-text">{row.name}</span>
        </span>
        <span className="sched-row-sub">{row.typeLabel} · {row.cadence}</span>
      </td>
      <td className="sched-cell-run">
        <span className="sched-run-next">{nextRun}</span>
        <span className="sched-row-sub">{fmt(row.lastRunAt)}</span>
      </td>
      <td><span className={`sol-badge sol-badge--${display.badgeVariant}`}>{display.badgeLabel}</span></td>
      <td className="sched-actions">
        {display.controls.length === 0 && <span className="sched-no-controls">—</span>}
        {display.controls.map((c) => {
          const key = `${row.id}|${c.op}`;
          const isPending = pendingKey === key;
          return (
            <button
              key={c.op}
              type="button"
              className="btn-ghost"
              disabled={!!pendingKey}
              aria-busy={isPending}
              onClick={() => onAction(row, c)}
            >
              {isPending ? c.pendingLabel : c.label}
            </button>
          );
        })}
      </td>
    </tr>
  );
}

function SchedGroup({ groupKey, rows, pendingKey, onAction, collapsible, open, onToggleOpen }) {
  if (rows.length === 0) return null;
  const isOpen = collapsible ? open : true;
  return (
    <section className="sched-group">
      {collapsible ? (
        <button type="button" className="sched-group-header" onClick={onToggleOpen} aria-expanded={isOpen}>
          <span className="sched-group-title">{GROUP_LABELS[groupKey]}</span>
          <span className="sched-group-count">{rows.length}</span>
        </button>
      ) : (
        <div className="sched-group-header sched-group-header--static">
          <span className="sched-group-title">{GROUP_LABELS[groupKey]}</span>
          <span className="sched-group-count">{rows.length}</span>
        </div>
      )}
      {isOpen && (
        <div className="sched-table-wrap">
          <table className="sol-table">
            <thead>
              <tr>
                <th>Name</th><th>Next / last run</th><th>Status</th><th className="sched-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <SchedRow key={row.id} row={row} pendingKey={pendingKey} onAction={onAction} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function ScheduledView() {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFinished, setShowFinished] = useState(false);
  const [engineOpen, setEngineOpen] = useState(readEngineOpenPref);
  const [pendingKey, setPendingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/scheduled`);
      const json = await res.json().catch(() => ({ success: false, error: 'Bad response from server' }));
      if (!res.ok || !json.success) throw new Error(json.error || `Failed to load (${res.status})`);
      setData(json.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const toggleEngineOpen = useCallback(() => {
    setEngineOpen((cur) => {
      const next = !cur;
      writeEngineOpenPref(next);
      return next;
    });
  }, []);

  // Refetches the whole view after every action rather than locally patching
  // state — a resume recomputes next_run_at server-side and a stale local
  // patch would show a wrong "next run" until the next unrelated refetch.
  const runAction = useCallback(async (row, control) => {
    if (pendingKey) return;
    const key = `${row.id}|${control.op}`;
    setPendingKey(key);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}${control.path}`, { method: control.method || 'POST' });
      const json = await res.json().catch(() => ({ success: false, error: 'Bad response from server' }));
      if (!res.ok || json.success === false) throw new Error(json.error || `Action failed (${res.status})`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setPendingKey(null);
    }
  }, [authFetch, load, pendingKey]);

  if (loading && !data) {
    return <p className="breathing-text">Loading schedule…</p>;
  }

  if (!data) {
    return (
      <div className="sched-view">
        {error && (
          <p className="error-text">
            {error}{' '}
            <button type="button" className="btn-ghost" onClick={() => { setError(null); load(); }}>Retry</button>
          </p>
        )}
      </div>
    );
  }

  const groups = groupRows(data.rows, { showFinished });
  const totalShown = GROUP_ORDER.reduce((n, k) => n + groups[k].length, 0);
  const unavailable = Object.entries(data.sources || {}).filter(([, v]) => v !== 'ok').map(([k]) => k);

  return (
    <div className="sched-view">
      {error && (
        <p className="error-text">
          {error}{' '}
          <button type="button" className="btn-ghost" onClick={() => setError(null)}>Dismiss</button>
        </p>
      )}

      {data.automationsDisabled === true && (
        <p className="sched-banner">
          Automations are switched off on the worker right now — scheduled automation runs will not
          fire until it&apos;s back on, so their next-run times are shown as unknown.
        </p>
      )}

      {unavailable.length > 0 && (
        <p className="sched-source-note">
          Some sources are unavailable right now: {unavailable.join(', ')}. Their rows are left out until
          the next successful read.
        </p>
      )}

      <div className="sched-toolbar">
        <label className="toggle">
          <input
            type="checkbox"
            checked={showFinished}
            onChange={(e) => setShowFinished(e.target.checked)}
          />
          <span>Show finished</span>
        </label>
      </div>

      {totalShown === 0 ? (
        <div className="sol-empty">
          <h3>Nothing scheduled</h3>
          <p>Automations and research tasks you schedule will show up here.</p>
        </div>
      ) : (
        <>
          {GROUP_ORDER.filter((k) => k !== 'engine').map((k) => (
            <SchedGroup
              key={k}
              groupKey={k}
              rows={groups[k]}
              pendingKey={pendingKey}
              onAction={runAction}
              collapsible={false}
            />
          ))}
          <SchedGroup
            groupKey="engine"
            rows={groups.engine}
            pendingKey={pendingKey}
            onAction={runAction}
            collapsible
            open={engineOpen}
            onToggleOpen={toggleEngineOpen}
          />
        </>
      )}
    </div>
  );
}
